<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from "@headlessui/vue";
import { ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: Boolean, required: true },     // controls open/close
  title: { type: String, default: "Confirm action" },
  message: { type: String, default: "Are you sure you want to continue?" },
  confirmText: { type: String, default: "Confirm" },
  cancelText: { type: String, default: "Cancel" },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const isOpen = ref(props.modelValue);

// keep prop and local ref in sync
watch(() => props.modelValue, v => (isOpen.value = v));
watch(isOpen, v => emit("update:modelValue", v));

function handleConfirm() {
  emit("confirm");
  isOpen.value = false;
}

function handleCancel() {
  emit("cancel");
  isOpen.value = false;
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="handleCancel">
      <!-- backdrop -->
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30" />
      </TransitionChild>

      <div class="fixed inset-0 flex items-center justify-center p-4">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <DialogTitle class="text-lg font-semibold text-gray-900">
              {{ title }}
            </DialogTitle>
            <p class="mt-2 text-sm text-gray-600">
              {{ message }}
            </p>

            <div class="mt-4 flex justify-end gap-3">
              <button
                type="button"
                class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                @click="handleCancel"
              >
                {{ cancelText }}
              </button>
              <button
                type="button"
                class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
                @click="handleConfirm"
              >
                {{ confirmText }}
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
